import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { QuestionCardData } from '../types';

interface QuestionCardProps {
  data: QuestionCardData;
  onSelect: (selection: Record<string, unknown>) => void;
}

export function QuestionCard({ data, onSelect }: QuestionCardProps) {
  const { t } = useTranslation();
  const [customAnswer, setCustomAnswer] = useState('');

  const handleOptionSelect = (optionId: string, title: string) => {
    onSelect({
      answer_type: 'option',
      option_id: optionId,
      title,
      answer: title,
      question: data.question,
    });
  };

  const handleCustomSubmit = () => {
    const answer = customAnswer.trim();
    if (!answer) return;

    onSelect({
      answer_type: 'custom',
      custom_answer: answer,
      answer,
      question: data.question,
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden my-2">
      <div className="p-4 space-y-3">
        {data.context && <p className="text-xs text-gray-500">{data.context}</p>}

        <p className="text-sm font-semibold text-gray-900">{data.question}</p>

        <div className="space-y-1.5">
          {data.options.map((opt) => (
            <button
              key={opt.option_id}
              onClick={() => handleOptionSelect(opt.option_id, opt.title)}
              className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors text-left"
            >
              {opt.icon && <span className="text-lg">{opt.icon}</span>}
              <div>
                <span className="text-sm font-medium text-gray-900">{opt.title}</span>
                {opt.description && (
                  <p className="text-xs text-gray-500 mt-0.5">{opt.description}</p>
                )}
              </div>
            </button>
          ))}
        </div>

        {data.allow_custom_answer !== false && (
          <div className="rounded-lg border border-dashed border-gray-300 p-3 space-y-2">
            <label className="block text-xs font-medium text-gray-600">
              {t('agentChat.cards.customAnswer', 'Custom answer')}
            </label>
            <input
              type="text"
              value={customAnswer}
              onChange={(e) => setCustomAnswer(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCustomSubmit();
                }
              }}
              placeholder={
                data.custom_answer_placeholder ||
                t('agentChat.cards.customAnswerPlaceholder', 'Type your answer')
              }
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <button
              onClick={handleCustomSubmit}
              disabled={!customAnswer.trim()}
              className="w-full py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {data.submit_custom_label ||
                t('agentChat.cards.submitCustomAnswer', 'Submit answer')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
